// src/pages/DeviceAndAgreementRegistry.js
// Redesigned page (inspired by your sample) using Tailwind classes + ethers v6.
// - Works with MetaMask or Trust Wallet (single injected provider)
// - Computes keccak256 of an uploaded PDF and submits its bytes32 to the contract
// - Replace DEFAULT_CONTRACT_ADDRESS with your deployed address

import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import {
    Filter,
    ChevronDown,
    ChevronUp,
    Search,
    Wallet as WalletIcon,
    FileText,
    Upload,
    Database,
    Bolt,
    CheckCircle2,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageFooter from "../components/PageFooter";

// ---------- Contract ABI (only used functions) ----------
const REGISTRY_ABI = [
    // --- Device ---
    {
        inputs: [
            { internalType: "uint256", name: "deviceNumber", type: "uint256" },
            { internalType: "int256", name: "deltaCounterValue", type: "int256" },
            { internalType: "int256", name: "cumulativeCounterValue", type: "int256" },
            { internalType: "uint64", name: "dataTimestamp", type: "uint64" },
        ],
        name: "recordDeviceData",
        outputs: [{ internalType: "uint256", name: "indexInDeviceLog", type: "uint256" }],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ internalType: "uint256", name: "deviceNumber", type: "uint256" }],
        name: "getDeviceDataCount",
        outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            { internalType: "uint256", name: "deviceNumber", type: "uint256" },
            { internalType: "uint256", name: "index", type: "uint256" },
        ],
        name: "getDeviceDataAt",
        outputs: [
            { internalType: "uint256", name: "_deviceNumber", type: "uint256" },
            { internalType: "int256", name: "_deltaCounterValue", type: "int256" },
            { internalType: "int256", name: "_cumulativeCounterValue", type: "int256" },
            { internalType: "uint64", name: "_dataTimestamp", type: "uint64" },
            { internalType: "uint64", name: "_recordedAt", type: "uint64" },
            { internalType: "address", name: "_reporter", type: "address" },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [
            { internalType: "uint256", name: "deviceNumber", type: "uint256" },
            { internalType: "uint256", name: "start", type: "uint256" },
            { internalType: "uint256", name: "count", type: "uint256" },
        ],
        name: "getDeviceDataRange",
        outputs: [
            {
                components: [
                    { internalType: "uint256", name: "deviceNumber", type: "uint256" },
                    { internalType: "int256", name: "deltaCounterValue", type: "int256" },
                    { internalType: "int256", name: "cumulativeCounterValue", type: "int256" },
                    { internalType: "uint64", name: "dataTimestamp", type: "uint64" },
                    { internalType: "uint64", name: "recordedAt", type: "uint64" },
                    { internalType: "address", name: "reporter", type: "address" },
                ],
                internalType: "struct DeviceAndAgreementRegistry.DeviceData[]",
                name: "out",
                type: "tuple[]",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ internalType: "uint256", name: "deviceNumber", type: "uint256" }],
        name: "getLatestDeviceData",
        outputs: [
            { internalType: "uint256", name: "_deviceNumber", type: "uint256" },
            { internalType: "int256", name: "_deltaCounterValue", type: "int256" },
            { internalType: "int256", name: "_cumulativeCounterValue", type: "int256" },
            { internalType: "uint64", name: "_dataTimestamp", type: "uint64" },
            { internalType: "uint64", name: "_recordedAt", type: "uint64" },
            { internalType: "address", name: "_reporter", type: "address" },
        ],
        stateMutability: "view",
        type: "function",
    },

    // --- Agreements (struct calldata) ---
    {
        inputs: [
            {
                components: [
                    { internalType: "uint256", name: "id", type: "uint256" },
                    { internalType: "string", name: "companyA", type: "string" },
                    { internalType: "address", name: "walletA", type: "address" },
                    { internalType: "string", name: "companyB", type: "string" },
                    { internalType: "address", name: "walletB", type: "address" },
                    { internalType: "uint64", name: "startTs", type: "uint64" },
                    { internalType: "uint64", name: "endTs", type: "uint64" },
                    { internalType: "uint256", name: "recurringPayment", type: "uint256" },
                    { internalType: "uint32", name: "every", type: "uint32" },
                    { internalType: "bytes32", name: "pdfHash", type: "bytes32" },
                    { internalType: "bool", name: "signedA", type: "bool" },
                    { internalType: "bool", name: "signedB", type: "bool" },
                    { internalType: "bool", name: "signedByBoth", type: "bool" },
                ],
                internalType: "struct DeviceAndAgreementRegistry.Agreement",
                name: "p",
                type: "tuple",
            },
        ],
        name: "createAgreement",
        outputs: [{ internalType: "uint256", name: "id", type: "uint256" }],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ internalType: "uint256", name: "agreementId", type: "uint256" }],
        name: "signAgreement",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ internalType: "uint256", name: "agreementId", type: "uint256" }],
        name: "getAgreement",
        outputs: [
            { internalType: "string", name: "companyA", type: "string" },
            { internalType: "address", name: "walletA", type: "address" },
            { internalType: "string", name: "companyB", type: "string" },
            { internalType: "address", name: "walletB", type: "address" },
            { internalType: "uint64", name: "startTs", type: "uint64" },
            { internalType: "uint64", name: "endTs", type: "uint64" },
            { internalType: "uint256", name: "recurringPayment", type: "uint256" },
            { internalType: "uint32", name: "every", type: "uint32" },
            { internalType: "bytes32", name: "pdfHash", type: "bytes32" },
            { internalType: "bool", name: "signedA", type: "bool" },
            { internalType: "bool", name: "signedB", type: "bool" },
            { internalType: "bool", name: "signedByBoth", type: "bool" },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ internalType: "address", name: "wallet", type: "address" }],
        name: "getAgreementsByWallet",
        outputs: [{ internalType: "uint256[]", name: "", type: "uint256[]" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ internalType: "uint256", name: "agreementId", type: "uint256" }],
        name: "isFullySigned",
        outputs: [{ internalType: "bool", name: "", type: "bool" }],
        stateMutability: "view",
        type: "function",
    },
];

// ---------- Replace with your deployed address ----------
const DEFAULT_CONTRACT_ADDRESS = "0x71dc451498577d6Bf4c2204c8D5495398FA0B558";

export default function DeviceAndAgreementRegistry() {
    // Wallet / Provider
    const [account, setAccount] = useState("");
    const [walletName, setWalletName] = useState("");
    const [provider, setProvider] = useState(null);
    const [signer, setSigner] = useState(null);

    // Contract
    const [contractAddr, setContractAddr] = useState(DEFAULT_CONTRACT_ADDRESS);
    const [registry, setRegistry] = useState(null);

    // UI state
    const [showFilters, setShowFilters] = useState(true);

    // Device inputs
    const [devDeviceNumber, setDevDeviceNumber] = useState("");
    const [devDelta, setDevDelta] = useState("");
    const [devCumulative, setDevCumulative] = useState("");
    const [devTs, setDevTs] = useState("");
    const [queryDeviceNumber, setQueryDeviceNumber] = useState("");
    const [rangeStart, setRangeStart] = useState("");
    const [rangeCount, setRangeCount] = useState("");
    const [deviceCount, setDeviceCount] = useState(null);
    const [latestData, setLatestData] = useState(null);
    const [rangeData, setRangeData] = useState([]);

    // Agreement inputs
    const [companyA, setCompanyA] = useState("Alpha Energy");
    const [companyB, setCompanyB] = useState("Beta Power");
    const [walletA, setWalletA] = useState("");
    const [walletB, setWalletB] = useState("");
    const [startTs, setStartTs] = useState("");
    const [endTs, setEndTs] = useState("");
    const [recurringEth, setRecurringEth] = useState("100");
    const [everyDays, setEveryDays] = useState("30");
    const [createdAgreementId, setCreatedAgreementId] = useState(null);
    const [signAgreementId, setSignAgreementId] = useState("");
    const [fetchAgreementId, setFetchAgreementId] = useState("");
    const [agreementView, setAgreementView] = useState(null);
    const [isSignedBoth, setIsSignedBoth] = useState(null);
    const [walletLookup, setWalletLookup] = useState("");
    const [agreementsByWallet, setAgreementsByWallet] = useState([]);

    // PDF hashing
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfHashHex, setPdfHashHex] = useState(null);

    const [walletState, setWalletState] = useState({
        account: null,
        walletName: null,
        connected: false,
        provider: null,
        signer: null,
    });

    useEffect(() => {
        setWalletA(account || "");
    }, [account]);

    const detectWalletName = (eth) => {
        if (!eth) return "None";
        if (eth.isMetaMask) return "MetaMask";
        if (eth.isTrust || eth.isTrustWallet) return "Trust Wallet";
        return "Injected";
    };

    // const connect = async () => {
    //     const eth = window.ethereum;
    //     if (!eth) {
    //         alert("No wallet detected. Install MetaMask or Trust Wallet, or use their in-app browser.");
    //         return;
    //     }
    //     const prov = new ethers.BrowserProvider(eth);
    //     await prov.send("eth_requestAccounts", []);
    //     const signer_ = await prov.getSigner();
    //     const addr = await signer_.getAddress();
    //     setProvider(prov);
    //     setSigner(signer_);
    //     setAccount(addr);
    //     setWalletName(detectWalletName(eth));
    // };

    const attach = async () => {
        if (!provider && !signer) {
            alert("Connect wallet first.");
            return;
        }
        const reg = new ethers.Contract(contractAddr, REGISTRY_ABI, signer || provider);
        setRegistry(reg);
    };

    // Helpers
    const toUnixSeconds = (dtLocal) => {
        if (!dtLocal) return null;
        return Math.floor(new Date(dtLocal).getTime() / 1000);
    };
    const fmtTime = (sec) => (sec ? new Date(sec * 1000).toLocaleString() : "");

    // PDF handler (compute keccak256)
    const onPdfSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setPdfFile(null);
            setPdfHashHex(null);
            return;
        }
        setPdfFile(file);
        const buf = await file.arrayBuffer();
        const hash = ethers.keccak256(new Uint8Array(buf));
        setPdfHashHex(hash);
    };

    // ----- Device actions -----
    const handleRecordDevice = async () => {
        if (!registry) return alert("Attach the contract first.");
        try {
            const dn = ethers.toBigInt(devDeviceNumber || "0");
            const delta = ethers.toBigInt(devDelta || "0"); // can be negative in string form
            const cum = ethers.toBigInt(devCumulative || "0");
            const ts = toUnixSeconds(devTs) ?? Math.floor(Date.now() / 1000);
            const tx = await registry.recordDeviceData(dn, delta, cum, ethers.toBigInt(ts));
            await tx.wait();
            alert("Device data recorded.");
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleGetCount = async () => {
        if (!registry) return;
        try {
            const c = await registry.getDeviceDataCount(ethers.toBigInt(queryDeviceNumber || "0"));
            setDeviceCount(c.toString());
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleGetLatest = async () => {
        if (!registry) return;
        try {
            const d = await registry.getLatestDeviceData(ethers.toBigInt(queryDeviceNumber || "0"));
            const obj = {
                deviceNumber: d._deviceNumber.toString(),
                deltaCounterValue: d._deltaCounterValue.toString(),
                cumulativeCounterValue: d._cumulativeCounterValue.toString(),
                dataTimestamp: Number(d._dataTimestamp),
                recordedAt: Number(d._recordedAt),
                reporter: d._reporter,
            };
            setLatestData(obj);
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleGetRange = async () => {
        if (!registry) return;
        try {
            const device = ethers.toBigInt(queryDeviceNumber || "0");
            const start = ethers.toBigInt(rangeStart || "0");
            const count = ethers.toBigInt(rangeCount || "10");
            const arr = await registry.getDeviceDataRange(device, start, count);
            const out = arr.map((d) => ({
                deviceNumber: d.deviceNumber.toString(),
                deltaCounterValue: d.deltaCounterValue.toString(),
                cumulativeCounterValue: d.cumulativeCounterValue.toString(),
                dataTimestamp: Number(d.dataTimestamp),
                recordedAt: Number(d.recordedAt),
                reporter: d.reporter,
            }));
            setRangeData(out);
        } catch (e) {
            // Contract may revert if start >= n; this will catch it.
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    // ----- Agreement actions -----
    const handleCreateAgreement = async () => {
        if (!registry) return alert("Attach the contract first.");
        try {
            const sTs = toUnixSeconds(startTs);
            const eTs = toUnixSeconds(endTs);
            if (!sTs || !eTs) return alert("Provide start/end timestamps.");
            if (!pdfHashHex) return alert("Please select a PDF to hash.");

            const agreement = {
                id: ethers.toBigInt(0), // ignored by contract; overwritten with _nextAgreementId
                companyA,
                walletA,
                companyB,
                walletB,
                startTs: ethers.toBigInt(sTs),
                endTs: ethers.toBigInt(eTs),
                recurringPayment: ethers.parseEther(recurringEth || "0"),
                every: Number(everyDays || "0"),
                pdfHash: pdfHashHex,
                signedA: false,
                signedB: false,
                signedByBoth: false,
            };

            // Predict ID via static call (optional)
            let predictedId = null;
            try {
                predictedId = await registry.createAgreement.staticCall(agreement);
            } catch { /* ignore */ }

            const tx = await registry.createAgreement(agreement);
            await tx.wait();

            if (predictedId) {
                setCreatedAgreementId(predictedId.toString());
            } else {
                const ids = await registry.getAgreementsByWallet(walletA);
                const lastId = ids.length ? ids[ids.length - 1].toString() : null;
                setCreatedAgreementId(lastId);
            }
            alert("Agreement created.");
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleSignAgreement = async () => {
        if (!registry) return;
        try {
            const id = ethers.toBigInt(signAgreementId || "0");
            const tx = await registry.signAgreement(id);
            await tx.wait();
            alert("Signed.");
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleGetAgreement = async () => {
        if (!registry) return;
        try {
            const id = ethers.toBigInt(fetchAgreementId || "0");
            const a = await registry.getAgreement(id);
            const view = {
                companyA: a.companyA,
                walletA: a.walletA,
                companyB: a.companyB,
                walletB: a.walletB,
                startTs: Number(a.startTs),
                endTs: Number(a.endTs),
                recurringPayment: a.recurringPayment.toString(),
                every: Number(a.every),
                pdfHash: a.pdfHash,
                signedA: a.signedA,
                signedB: a.signedB,
                signedByBoth: a.signedByBoth,
            };
            setAgreementView(view);
            const s = await registry.isFullySigned(id);
            setIsSignedBoth(!!s);
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const handleGetByWallet = async () => {
        if (!registry) return;
        try {
            const ids = await registry.getAgreementsByWallet(walletLookup);
            setAgreementsByWallet(ids.map((x) => x.toString()));
        } catch (e) {
            console.error(e);
            alert(e.shortMessage || e.message);
        }
    };

    const connected = !!account;
    const attached = !!registry;

    const handleLogin = () => {
        // e.g., navigate("/login") if using react-router
        window.location.href = "/login";
    };
    
    return (
        <div className="min-h-screen flex flex-col bg-gray-50">

            <PageHeader
                onLogin={handleLogin}
                walletState={walletState}
                onWalletStateChange={setWalletState}
            />
            
            {/*/!* --- Header --- *!/*/}
            {/*<div className="sticky top-0 z-30 bg-white border-b">*/}
            {/*    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">*/}
            {/*        <div className="flex items-center gap-2">*/}
            {/*            <Bolt className="h-5 w-5 text-indigo-600" />*/}
            {/*            <span className="font-semibold">Energy Production Marketplace</span>*/}
            {/*        </div>*/}
            {/*        <div className="flex items-center gap-3">*/}
            {/*            <div className="hidden md:flex items-center text-sm text-gray-500 gap-1">*/}
            {/*                <WalletIcon className="h-4 w-4" />*/}
            {/*                <span className="truncate max-w-[180px]">{account || "Not connected"}</span>*/}
            {/*                <span className="text-gray-400">•</span>*/}
            {/*                <span>{walletName || "-"}</span>*/}
            {/*            </div>*/}
            {/*            <button*/}
            {/*                onClick={connect}*/}
            {/*                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium*/}
            {/*             bg-indigo-600 text-white hover:bg-indigo-700"*/}
            {/*            >*/}
            {/*                {connected ? "Connected" : "Connect Wallet"}*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Header spacer */}
            <div className="h-2" />

            {/* --- Page Intro --- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <h1 className="text-2xl font-bold">Device & Agreement Registry</h1>
                <p className="text-gray-600 mt-1">
                    Record device readings, manage agreements, and verify PDF signatures on-chain.
                </p>
            </div>

            {/* --- Filters / Controls Bar --- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
                <div className="bg-white rounded-xl shadow-sm border p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <Filter className="h-4 w-4" />
                            Controls
                        </div>
                        <button
                            className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                            onClick={() => setShowFilters((s) => !s)}
                        >
                            {showFilters ? (
                                <>
                                    Hide <ChevronUp className="h-4 w-4" />
                                </>
                            ) : (
                                <>
                                    Show <ChevronDown className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {/* Contract address */}
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Contract address</label>
                                <input
                                    value={contractAddr}
                                    onChange={(e) => setContractAddr(e.target.value)}
                                    placeholder="0x…"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex items-end gap-3">
                                <button
                                    onClick={attach}
                                    className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black"
                                >
                                    {attached ? "Attached" : "Attach"}
                                </button>
                            </div>

                            {/* Quick device search */}
                            <div className="relative">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Device #</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        value={queryDeviceNumber}
                                        onChange={(e) => setQueryDeviceNumber(e.target.value)}
                                        placeholder="e.g., 1001"
                                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Range start</label>
                                <input
                                    value={rangeStart}
                                    onChange={(e) => setRangeStart(e.target.value)}
                                    placeholder="0"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Range count</label>
                                <input
                                    value={rangeCount}
                                    onChange={(e) => setRangeCount(e.target.value)}
                                    placeholder="10"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex items-end gap-2">
                                <button
                                    onClick={handleGetCount}
                                    className="px-3 py-2 rounded-md bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100"
                                >
                                    Count
                                </button>
                                <button
                                    onClick={handleGetLatest}
                                    className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                                >
                                    Latest
                                </button>
                                <button
                                    onClick={handleGetRange}
                                    className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200"
                                >
                                    Range
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- Results badge --- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-2 text-sm text-gray-500">
                Device count:&nbsp;
                <span className="font-medium text-gray-700">{deviceCount ?? "-"}</span>
            </div>

            {/* --- Main Grid --- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Device Card */}
                    <div className="bg-white rounded-xl shadow-sm border p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Database className="h-5 w-5 text-gray-700" />
                            <h3 className="font-semibold">Device Readings</h3>
                        </div>

                        {/* Record form */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Device #</label>
                                <input
                                    value={devDeviceNumber}
                                    onChange={(e) => setDevDeviceNumber(e.target.value)}
                                    placeholder="e.g., 1001"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Delta (int)</label>
                                <input
                                    value={devDelta}
                                    onChange={(e) => setDevDelta(e.target.value)}
                                    placeholder="e.g., 10"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Cumulative (int)</label>
                                <input
                                    value={devCumulative}
                                    onChange={(e) => setDevCumulative(e.target.value)}
                                    placeholder="e.g., 110"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Timestamp</label>
                                <input
                                    type="datetime-local"
                                    value={devTs}
                                    onChange={(e) => setDevTs(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            <button
                                onClick={handleRecordDevice}
                                className="px-3 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black"
                            >
                                Record Device Data
                            </button>
                        </div>

                        {/* Latest */}
                        {latestData && (
                            <div className="mt-5 bg-gray-50 border rounded-lg p-3 text-sm">
                                <div className="font-medium mb-1">Latest Reading</div>
                                <div className="text-gray-700">
                                    #{latestData.deviceNumber} • Δ {latestData.deltaCounterValue} • Σ {latestData.cumulativeCounterValue}
                                </div>
                                <div className="text-gray-500">
                                    Data: {fmtTime(latestData.dataTimestamp)} — Recorded: {fmtTime(latestData.recordedAt)} — By:{" "}
                                    <span className="font-mono">{latestData.reporter}</span>
                                </div>
                            </div>
                        )}

                        {/* Range list */}
                        {rangeData.length > 0 && (
                            <div className="mt-5">
                                <div className="text-sm font-medium mb-2">Range Results</div>
                                <div className="max-h-64 overflow-auto border rounded-lg">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Device</th>
                                            <th className="px-3 py-2 text-left">Δ</th>
                                            <th className="px-3 py-2 text-left">Σ</th>
                                            <th className="px-3 py-2 text-left">Data ts</th>
                                            <th className="px-3 py-2 text-left">Recorded</th>
                                            <th className="px-3 py-2 text-left">Reporter</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {rangeData.map((r, i) => (
                                            <tr key={i} className="border-t">
                                                <td className="px-3 py-2">{r.deviceNumber}</td>
                                                <td className="px-3 py-2">{r.deltaCounterValue}</td>
                                                <td className="px-3 py-2">{r.cumulativeCounterValue}</td>
                                                <td className="px-3 py-2">{fmtTime(r.dataTimestamp)}</td>
                                                <td className="px-3 py-2">{fmtTime(r.recordedAt)}</td>
                                                <td className="px-3 py-2 font-mono">{r.reporter}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Agreements Card */}
                    <div className="bg-white rounded-xl shadow-sm border p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-5 w-5 text-gray-700" />
                            <h3 className="font-semibold">Agreements</h3>
                        </div>

                        {/* Create agreement */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Company A</label>
                                <input
                                    value={companyA}
                                    onChange={(e) => setCompanyA(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Company B</label>
                                <input
                                    value={companyB}
                                    onChange={(e) => setCompanyB(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Wallet A</label>
                                <input
                                    value={walletA}
                                    onChange={(e) => setWalletA(e.target.value)}
                                    placeholder="0x…"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Wallet B</label>
                                <input
                                    value={walletB}
                                    onChange={(e) => setWalletB(e.target.value)}
                                    placeholder="0x…"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                                <input
                                    type="datetime-local"
                                    value={startTs}
                                    onChange={(e) => setStartTs(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                                <input
                                    type="datetime-local"
                                    value={endTs}
                                    onChange={(e) => setEndTs(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Recurring (ETH)</label>
                                <input
                                    value={recurringEth}
                                    onChange={(e) => setRecurringEth(e.target.value)}
                                    placeholder="e.g., 100"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Every (days)</label>
                                <input
                                    value={everyDays}
                                    onChange={(e) => setEveryDays(e.target.value)}
                                    placeholder="e.g., 30"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Signed PDF</label>
                                <div className="flex items-center gap-3">
                                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer hover:bg-gray-50">
                                        <Upload className="h-4 w-4 text-gray-600" />
                                        <span className="text-sm">Choose PDF</span>
                                        <input type="file" accept="application/pdf" onChange={onPdfSelect} className="hidden" />
                                    </label>
                                    <div className="text-xs text-gray-600 truncate">
                                        {pdfFile ? pdfFile.name : "No file selected"}
                                    </div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1 break-all">
                                    {pdfHashHex ? `keccak256: ${pdfHashHex}` : "Pick a PDF to compute its keccak256 hash"}
                                </div>
                            </div>
                        </div>

                        <div className="mt-3">
                            <button
                                onClick={handleCreateAgreement}
                                className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                            >
                                Create Agreement
                            </button>
                            <span className="ml-3 text-sm text-gray-600">
                Created ID: <span className="font-medium">{createdAgreementId ?? "-"}</span>
              </span>
                        </div>

                        {/* Sign & Fetch */}
                        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Sign */}
                            <div className="bg-gray-50 border rounded-lg p-4 flex flex-col gap-3">
                                <div className="text-sm font-medium flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    <span>Sign Agreement</span>
                                </div>

                                {/* Stack on small screens, row on >=sm; allow wrapping */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                    <input
                                        value={signAgreementId}
                                        onChange={(e) => setSignAgreementId(e.target.value)}
                                        placeholder="Agreement ID"
                                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <button
                                        onClick={handleSignAgreement}
                                        className="w-full sm:w-auto px-3 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black"
                                    >
                                        Sign
                                    </button>
                                </div>

                                <p className="text-xs text-gray-500">
                                    Only Wallet&nbsp;A or Wallet&nbsp;B can sign the agreement.
                                </p>
                            </div>

                            {/* Get */}
                            <div className="bg-gray-50 border rounded-lg p-4 flex flex-col gap-3">
                                <div className="text-sm font-medium">Get Agreement</div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                    <input
                                        value={fetchAgreementId}
                                        onChange={(e) => setFetchAgreementId(e.target.value)}
                                        placeholder="Agreement ID"
                                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <button
                                        onClick={handleGetAgreement}
                                        className="w-full sm:w-auto px-3 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200"
                                    >
                                        Fetch
                                    </button>
                                </div>

                                {agreementView && (
                                    <div className="mt-2 text-sm space-y-1 break-words">
                                        <div className="font-medium">
                                            {agreementView.companyA}{" "}
                                            (<span className="font-mono break-all">{agreementView.walletA}</span>) ↔{" "}
                                            {agreementView.companyB}{" "}
                                            (<span className="font-mono break-all">{agreementView.walletB}</span>)
                                        </div>

                                        <div className="text-gray-700">
                                            Recurring: {agreementView.recurringPayment} wei • every {agreementView.every} days
                                        </div>

                                        <div className="text-gray-700">
                                            Period: {fmtTime(agreementView.startTs)} → {fmtTime(agreementView.endTs)}
                                        </div>

                                        {/* Force long hashes to wrap */}
                                        <div className="text-gray-500 break-all">PDF: {agreementView.pdfHash}</div>

                                        {/* Wrap status badges */}
                                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                                            <span>Signed A: {agreementView.signedA ? "✔" : "✖"}</span>
                                            <span>Signed B: {agreementView.signedB ? "✔" : "✖"}</span>
                                            <span>Both: {agreementView.signedByBoth ? "✔" : "✖"}</span>
                                            <span className="text-gray-500">
                                                Status: {isSignedBoth === null ? "-" : isSignedBoth ? "fully signed" : "pending"}
                                              </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* By wallet */}
                        <div className="mt-6 bg-gray-50 border rounded-lg p-3">
                            <div className="text-sm font-medium mb-2">Agreements by Wallet</div>
                            <div className="flex items-center gap-2">
                                <input
                                    value={walletLookup}
                                    onChange={(e) => setWalletLookup(e.target.value)}
                                    placeholder="0x…"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                                />
                                <button
                                    onClick={handleGetByWallet}
                                    className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200"
                                >
                                    List
                                </button>
                            </div>
                            {agreementsByWallet.length > 0 && (
                                <div className="text-sm text-gray-700 mt-2">
                                    IDs: {agreementsByWallet.join(", ")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/*/!* --- Footer --- *!/*/}
            {/*<div className="mt-auto border-t bg-white">*/}
            {/*    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between text-sm text-gray-500">*/}
            {/*        <div className="flex items-center gap-2">*/}
            {/*            <Bolt className="h-4 w-4" />*/}
            {/*            <span>On-chain Device & Agreement Registry</span>*/}
            {/*        </div>*/}
            {/*        <div>© {new Date().getFullYear()}</div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            <PageFooter />
            
        </div>
    );
}
